import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { TiltPanel } from '@carouselgg/nexus/tilt-panel'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { FeatureCard } from '@carouselgg/nexus/feature-card'
import { MatchTicker } from '@carouselgg/nexus/match-ticker'
import { Bracket } from '@carouselgg/nexus/bracket'
import { SeasonTimeline } from '@carouselgg/nexus/season-timeline'
import {
  CalendarDays, ClipboardList, Trophy, Users, BarChart3, Megaphone,
} from '@carouselgg/nexus/icon'
import { useTrackVisibility } from '../hooks/useTrackVisibility'
import {
  demoMatches, demoBracket, demoTimeline, demoSeasonNow, demoStandings,
} from './demoData'
import styles from './Product.module.css'

const FEATURES = [
  {
    icon: <ClipboardList size={22} />,
    title: 'Registration',
    description: 'Applications, rosters and eligibility, screened by Trust Score before you accept.',
    accent: 'var(--nexus-purple)',
  },
  {
    icon: <CalendarDays size={22} />,
    title: 'Scheduling',
    description: 'Fixtures, reschedules and calendar sync that do not live in a pinned Discord message.',
    accent: 'var(--nexus-pink)',
  },
  {
    icon: <Trophy size={22} />,
    title: 'Playoffs',
    description: 'Brackets seed themselves from the table. Single or double elimination, tiebreakers included.',
    accent: 'var(--nexus-blue)',
  },
  {
    icon: <BarChart3 size={22} />,
    title: 'Stats',
    description: 'Verified performance from organised play — the record that follows a player between leagues.',
    accent: 'var(--nexus-green)',
  },
  {
    icon: <Users size={22} />,
    title: 'Teams & orgs',
    description: 'Multiple teams per game and per elo, with roster limits and substitutes handled.',
    accent: 'var(--nexus-yellow)',
  },
  {
    icon: <Megaphone size={22} />,
    title: 'Your own page',
    description: 'A white-labelled public page with standings, news and a live match ticker.',
    accent: 'var(--nexus-purple)',
  },
]

export function Product() {
  const ref = useTrackVisibility<HTMLDivElement>('product')

  return (
    <AngledBand id="product" tone="base" slice="bottom" reverse>
      <div className={styles.inner} ref={ref}>
        <ScrollReveal>
          <div className={styles.head}>
            <div>
              <p className={styles.eyebrow}>
                <span className={styles.seed}>04</span>
                <span className={styles.rule} />
                League operations
              </p>
              <DisplayHeading size="6xl">
                Run the whole{' '}
                <br />
                <GradientText gradient="brand" angle={115}>season.</GradientText>
              </DisplayHeading>
            </div>
            <p className={styles.lede}>
              Registration, scheduling, standings, playoffs and results in one place — instead of a
              spreadsheet, three Discord bots and a volunteer who burns out by week six.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={80}>
          <div className={styles.stage}>
            <TiltPanel className={styles.panel} facing="right" label="Spring Split — Gold division">
              <div className={styles.ticker}>
                <MatchTicker events={demoMatches} />
              </div>

              <div className={styles.split}>
                <div>
                  <h3 className={styles.blockTitle}>
                    Playoffs
                    <span className={styles.blockMeta}>Single elimination · 8 teams</span>
                  </h3>
                  <div className={styles.bracketScroll}>
                    <Bracket sections={demoBracket} variant="playoff" />
                  </div>
                </div>

                <div className={styles.rail}>
                  <div>
                    <h3 className={styles.blockTitle}>Season timeline</h3>
                    <SeasonTimeline segments={demoTimeline} now={demoSeasonNow} compact />
                  </div>

                  <div>
                    <h3 className={styles.blockTitle}>
                      Standings
                      <span className={styles.blockMeta}>Division A</span>
                    </h3>
                    <div className={styles.table}>
                      <div className={`${styles.row} ${styles.headRow}`}>
                        <span>#</span>
                        <span>Team</span>
                        <span className={styles.record}>W–L</span>
                        <span className={styles.diff}>Diff</span>
                      </div>
                      {demoStandings.map((row) => (
                        <div key={row.team} className={styles.row}>
                          <span
                            className={`${styles.pos} ${row.pos <= 4 ? styles.qualified : ''}`}
                          >
                            {row.pos}
                          </span>
                          <span className={styles.team}>{row.team}</span>
                          <span className={styles.record}>{row.record}</span>
                          <span
                            className={`${styles.diff} ${row.up ? styles.diffUp : styles.diffDown}`}
                          >
                            {row.diff}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TiltPanel>
          </div>
        </ScrollReveal>

        <div className={styles.features}>
          {FEATURES.map((feature, index) => (
            <ScrollReveal key={feature.title} delay={(index % 3) * 70}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                accent={feature.accent}
              />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </AngledBand>
  )
}
